import React, { useEffect, useState, Suspense, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dock } from './Dock';
import { Window } from './Window';
import { CommandPalette } from './CommandPalette';
import { BackgroundCustomizer } from './BackgroundCustomizer';
import { WebGLBackground } from './WebGLBackground';
import { AICompanion } from './AICompanion';
import { SEOHead } from '../ui/SEOHead';
import { SkipNavigation } from '../ui/SkipNavigation';
import { useWindows } from '../../contexts/WindowContext';
import { useTheme } from '../../contexts/ThemeContext';
import { DesktopWidgets } from './DesktopWidgets';
import { MobileWidgets } from './MobileWidgets';
import { useNavigate, useLocation } from 'react-router-dom';
import { StartupAnimation } from './StartupAnimation';
// Lazy load app components for better performance
const AboutApp = lazy(() => import('../apps/AboutApp').then(module => ({
  default: module.AboutApp
})));
const ExpertiseApp = lazy(() => import('../apps/ExpertiseApp').then(module => ({
  default: module.ExpertiseApp
})));
const PortfolioApp = lazy(() => import('../apps/PortfolioApp').then(module => ({
  default: module.PortfolioApp
})));
const ProcessApp = lazy(() => import('../apps/ProcessApp').then(module => ({
  default: module.ProcessApp
})));
const SettingsApp = lazy(() => import('../apps/SettingsApp').then(module => ({
  default: module.SettingsApp
})));
const AIAssistantApp = lazy(() => import('../apps/AIAssistantApp').then(module => ({
  default: module.AIAssistantApp
})));
const ContactApp = lazy(() => import('../apps/ContactApp').then(module => ({
  default: module.ContactApp
})));
// Loading fallback component
function AppLoader() {
  const {
    getThemeClasses
  } = useTheme();
  const themeClasses = getThemeClasses();
  return <div className={`flex items-center justify-center h-full ${themeClasses.text}`} role="status" aria-live="polite" aria-label="Loading application" data-id="element-681">
      <div className="text-center" data-id="element-682">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" aria-hidden="true" data-id="element-683" />
        <p className="text-sm opacity-70" data-id="element-684">Loading...</p>
      </div>
    </div>;
}
export function Desktop() {
  const {
    windows,
    openWindow,
    isMobile
  } = useWindows();
  const {
    t,
    getThemeClasses,
    theme,
    highContrast,
    background
  } = useTheme();
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showBackgroundCustomizer, setShowBackgroundCustomizer] = useState(false);
  const [showStartup, setShowStartup] = useState(false);
  const themeClasses = getThemeClasses();
  const navigate = useNavigate();
  const location = useLocation();
  // Check if user has seen startup animation
  useEffect(() => {
    const hasSeenStartup = localStorage.getItem('hasSeenStartup') === 'true';
    if (!hasSeenStartup && location.pathname === '/') {
      setShowStartup(true);
    }
  }, [location.pathname]);
  const handleStartupComplete = () => {
    setShowStartup(false);
    localStorage.setItem('hasSeenStartup', 'true');
  };
  // Get SEO data based on current route
  const getSEOData = () => {
    const path = location.pathname.slice(1);
    const seoData: Record<string, {
      title: string;
      description: string;
    }> = {
      about: {
        title: 'About',
        description: 'Learn about Mick van Lier, a UX Designer and AI Developer specializing in translating complex problems into elegant solutions.'
      },
      expertise: {
        title: 'Expertise',
        description: 'Explore expertise in UX Design, AI Integration, Development, and Systems Thinking.'
      },
      portfolio: {
        title: 'Portfolio',
        description: 'View projects showcasing the translation process from complex questions to elegant solutions.'
      },
      process: {
        title: 'Process',
        description: 'Discover the 5-phase translation process: from question analysis to iteration and improvement.'
      },
      contact: {
        title: 'Contact',
        description: 'Get in touch to discuss how complex challenges can be translated into elegant solutions.'
      },
      settings: {
        title: 'Settings',
        description: 'Customize your experience with theme and language options.'
      },
      'ai-assistant': {
        title: 'AI Assistant',
        description: 'Chat with the AI assistant to learn more about expertise and projects.'
      }
    };
    return seoData[path] || {};
  };
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setShowBackgroundCustomizer(prev => !prev);
      }
      // Escape key to close overlays
      if (e.key === 'Escape') {
        if (showCommandPalette) setShowCommandPalette(false);
        if (showBackgroundCustomizer) setShowBackgroundCustomizer(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCommandPalette, showBackgroundCustomizer]);
  // Sync URL with windows
  useEffect(() => {
    const path = location.pathname.slice(1);
    if (path && path !== '') {
      const appId = path;
      const appTitles: Record<string, string> = {
        about: 'About Mick',
        expertise: t.apps.expertise || 'Expertise',
        portfolio: t.apps.portfolio || 'Portfolio',
        process: t.apps.process || 'Werkwijze',
        contact: t.apps.contact || 'Contact',
        settings: t.apps.settings || 'Settings',
        'ai-assistant': t.apps.aiAssistant || 'AI Assistant'
      };
      if (appTitles[appId]) {
        const existingWindow = windows.find(w => w.appId === appId);
        if (!existingWindow) {
          openWindow(appId, appTitles[appId]);
        } else if (existingWindow.isMinimized) {
          openWindow(appId, appTitles[appId]);
        }
      } else {
        navigate('/', {
          replace: true
        });
      }
    }
  }, [location.pathname, windows, openWindow, t.apps, navigate]);
  // Update URL when windows change
  useEffect(() => {
    if (windows.length > 0) {
      const activeWindows = windows.filter(w => !w.isMinimized);
      if (activeWindows.length > 0) {
        const topWindow = activeWindows.reduce((prev, current) => current.zIndex > prev.zIndex ? current : prev, activeWindows[0]);
        if (location.pathname !== `/${topWindow.appId}`) {
          navigate(`/${topWindow.appId}`, {
            replace: true
          });
        }
      } else if (location.pathname !== '/') {
        navigate('/', {
          replace: true
        });
      }
    } else if (location.pathname !== '/') {
      navigate('/', {
        replace: true
      });
    }
  }, [windows, navigate, location.pathname]);
  // Auto-open About window when desktop loads
  useEffect(() => {
    const hasOpenedAbout = localStorage.getItem('hasOpenedAbout') === 'true';
    const hasSeenStartup = localStorage.getItem('hasSeenStartup') === 'true';
    if (!hasOpenedAbout && hasSeenStartup && location.pathname === '/') {
      setTimeout(() => {
        openWindow('about', 'About Mick');
        navigate('/about', {
          replace: true
        });
        localStorage.setItem('hasOpenedAbout', 'true');
      }, 500);
    }
  }, [location.pathname, navigate, openWindow]);
  const getAppComponent = (appId: string) => {
    switch (appId) {
      case 'about':
        return <AboutApp data-id="element-685" />;
      case 'expertise':
        return <ExpertiseApp data-id="element-686" />;
      case 'portfolio':
        return <PortfolioApp data-id="element-687" />;
      case 'process':
        return <ProcessApp data-id="element-688" />;
      case 'contact':
        return <ContactApp data-id="element-689" />;
      case 'settings':
        return <SettingsApp data-id="element-690" />;
      case 'ai-assistant':
        return <AIAssistantApp data-id="element-691" />;
      default:
        return <div data-id="element-692">App not found</div>;
    }
  };
  const seoData = getSEOData();
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="fixed inset-0 overflow-hidden" data-id="element-693">
      <SkipNavigation data-id="element-694" />
      <SEOHead title={seoData.title} description={seoData.description} data-id="element-695" />
      {/* Show startup animation if needed */}
      <AnimatePresence data-id="element-696">
        {showStartup && <StartupAnimation onComplete={handleStartupComplete} data-id="element-697" />}
      </AnimatePresence>
      <div className={`absolute inset-0 ${theme === 'light' && !highContrast ? `bg-gradient-to-br ${background}` : themeClasses.bg}`} role="presentation" data-id="element-698">
        {theme === 'light' && !highContrast && <div className="absolute inset-0 bg-white/85 backdrop-blur-sm" data-id="element-699" />}
        {!highContrast && <WebGLBackground data-id="element-700" />}
        {highContrast && <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, currentColor 10px, currentColor 11px)`
      }} aria-hidden="true" data-id="element-701" />}
      </div>
      {!isMobile && <button onClick={() => setShowBackgroundCustomizer(true)} className={`absolute top-4 right-4 px-4 py-2 backdrop-blur-xl ${themeClasses.card} border ${themeClasses.border} rounded-lg ${themeClasses.text} text-sm font-medium transition-all shadow-lg z-10 ${themeClasses.cardHover} hover:scale-105`} aria-label="Customize background appearance" data-id="element-702">
          {t.desktop.customizeBackground}
        </button>}
      <main id="main-content" className="relative w-full h-full" role="main" data-id="element-703">
        {!isMobile && windows.length === 0 && <DesktopWidgets data-id="element-704" />}
        {isMobile && windows.length === 0 && <MobileWidgets data-id="element-705" />}
        <div className="relative w-full h-full" data-id="element-706">
          {windows.map(window => <Window key={window.id} window={window} data-id="element-707">
              <Suspense fallback={<AppLoader data-id="element-709" />} data-id="element-708">
                {getAppComponent(window.appId)}
              </Suspense>
            </Window>)}
        </div>
      </main>
      {showCommandPalette && <CommandPalette onClose={() => setShowCommandPalette(false)} data-id="element-710" />}
      {showBackgroundCustomizer && <BackgroundCustomizer onClose={() => setShowBackgroundCustomizer(false)} data-id="element-711" />}
      <AICompanion data-id="element-712" />
      <Dock data-id="element-713" />
    </motion.div>;
}